import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  CreditCard,
  Folder,
  FileCheck,
  ArrowRight,
  Loader,
  Upload,
  Sparkles,
  BookOpen,
  BarChart3,
  Activity,
  Target,
  Scale,
  Search,
} from 'lucide-react';
import { reportApi } from '../services/reportApi';
import { httpClient } from '../services/httpClient';
import { caseApi } from '../services/api';
import { creditApi } from '../services/creditApi';
import { useOpenCases } from '../contexts/OpenCasesContext';
import { useAuth } from '../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';

interface DashboardStats {
  totalCases: number;
  totalReports: number;
  totalDocuments: number;
  totalPages: number;
  creditsRemaining: number;
}

interface RecentCase {
  id: string;
  name: string;
  status: string;
  updatedAt: string;
  documentCount: number;
}

interface RecentReport {
  id: string;
  title: string;
  caseId: string;
  caseName: string;
  createdAt: string;
  status: string;
}

export const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { openCase } = useOpenCases();
  const [createError, setCreateError] = useState<string | null>(null);

  const createCaseMutation = useMutation(
    () => caseApi.createCase({ name: 'Untitled Case' }),
    {
      onSuccess: (data) => {
        openCase(data.caseId, 'Untitled Case');
        navigate(`/cases/${data.caseId}/records`);
      },
      onError: () => {
        setCreateError('Failed to create case. Please try again.');
        setTimeout(() => setCreateError(null), 4000);
      },
    }
  );

  const handleNewCase = () => createCaseMutation.mutate();

  const welcomeName = user?.title && user?.lastName
    ? `${user.title} ${user.lastName}`
    : user?.firstName || 'User';

  const getStatusIcon = (status: string) => {
    const normalizedStatus = status?.toUpperCase() || 'CREATED';
    switch (normalizedStatus) {
      case 'CREATED':
        return <FileText className="h-4 w-4 text-gray-500" />;
      case 'UPLOADING':
        return <Loader className="h-4 w-4 text-cyan-500 animate-spin" />;
      case 'PROCESSING':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'QUEUED':
        return <Clock className="h-4 w-4 text-orange-500" />;
      case 'READY':
        return <CheckCircle className="h-4 w-4 text-cyan-500" />;
      case 'ANALYSING':
        return <Loader className="h-4 w-4 text-purple-500 animate-spin" />;
      case 'PARTIAL':
        return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      case 'PROCESSED':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      default:
        return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusText = (status: string) => {
    const normalizedStatus = status?.toUpperCase() || 'CREATED';
    switch (normalizedStatus) {
      case 'CREATED':
        return 'Created';
      case 'UPLOADING':
        return 'Uploading';
      case 'PROCESSING':
        return 'Processing';
      case 'QUEUED':
        return 'Queued';
      case 'READY':
        return 'Ready';
      case 'ANALYSING':
        return 'Analysing';
      case 'PARTIAL':
        return 'Partial';
      case 'PROCESSED':
        return 'Processed';
      default:
        return 'Unknown';
    }
  };

  const getStatusColor = (status: string) => {
    const normalizedStatus = status?.toUpperCase() || 'CREATED';
    switch (normalizedStatus) {
      case 'CREATED':
        return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
      case 'UPLOADING':
        return 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300';
      case 'PROCESSING':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'QUEUED':
        return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300';
      case 'READY':
        return 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300';
      case 'ANALYSING':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
      case 'PARTIAL':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
      case 'PROCESSED':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  // Fetch dashboard stats
  const { data: stats, isLoading: statsLoading } = useQuery(
    'dashboardStats',
    async () => {
      // Fetch cases, reports, and credit balance in parallel
      const [casesResponse, reports, creditBalance] = await Promise.all([
        httpClient.get<any>('/cases', { page: 0, size: 100 }),
        reportApi.getUserReports().catch((err) => {
          console.error('Failed to fetch reports for dashboard stats:', err);
          return [] as any[];
        }),
        creditApi.getBalance().catch((err) => {
          console.error('Failed to fetch credit balance:', err);
          return null;
        }),
      ]);
      const cases = casesResponse.content || [];

      // Calculate stats
      const totalDocuments = cases.reduce((sum: number, c: any) => sum + (c.documentCount || 0), 0);

      // Sum page counts from all documents across all cases
      let totalPages = 0;
      try {
        const allDocs = await Promise.all(
          cases.map((c: any) =>
            httpClient.get<any[]>(`/documents/cases/${c.id}/documents`).catch(() => [])
          )
        );
        totalPages = allDocs
          .flat()
          .reduce((sum: number, doc: any) => sum + (doc.pageCount || 0), 0);
      } catch {
        // Fall back to 0 if document fetch fails
      }

      return {
        totalCases: cases.length,
        totalReports: reports.length,
        totalDocuments,
        totalPages,
        creditsRemaining: creditBalance?.freePageBalance ?? 0,
      } as DashboardStats;
    }
  );

  // Fetch recent cases
  const { data: recentCases, isLoading: casesLoading } = useQuery(
    'recentCases',
    async () => {
      const response = await httpClient.get<any>('/cases', { 
        page: 0, 
        size: 5,
        sort: 'updatedAt,desc'
      });
      const cases = response.content || [];
      
      // Fetch actual document counts and chronology status for each case
      const casesWithDetails = await Promise.all(
        cases.map(async (caseItem: any) => {
          try {
            // Get documents
            const docs = await httpClient.get<any>(`/documents/cases/${caseItem.id}/documents`);
            const actualDocCount = Array.isArray(docs) ? docs.length : 0;
            
            // Check if chronology exists
            let hasChronology = false;
            try {
              const chronology = await httpClient.get<any>(`/chronologies/case/${caseItem.id}`);
              hasChronology = chronology && chronology.events && chronology.events.length > 0;
            } catch (err: any) {
              // 404 is expected for cases without chronology
              if (err?.response?.status && err.response.status !== 404) {
                console.error(`Chronology service error for case ${caseItem.id} (HTTP ${err.response.status}):`, err);
              }
              hasChronology = false;
            }

            // Check if metadata extraction is actively running
            let isAnalysing = false;
            try {
              const progress = await httpClient.get<{ complete: boolean; failed: boolean }>(
                `/intelligence/metadata/progress/${caseItem.id}`
              );
              if (progress && !progress.complete && !progress.failed) {
                isAnalysing = true;
              }
            } catch (err: any) {
              // 204/404 is expected when no extraction has run
              if (err?.response?.status && err.response.status !== 404 && err.response.status !== 204) {
                console.error(`Metadata progress error for case ${caseItem.id} (HTTP ${err.response.status}):`, err);
              }
            }

            // Compute display status
            let displayStatus = caseItem.status;
            if (caseItem.status === 'CREATED' || caseItem.status === 'QUEUED') {
              if (actualDocCount === 0) {
                displayStatus = 'CREATED';
              } else if (isAnalysing) {
                displayStatus = 'ANALYSING';
              } else if (hasChronology) {
                displayStatus = 'PROCESSED';
              } else {
                displayStatus = 'READY';
              }
            }

            return {
              ...caseItem,
              documentCount: actualDocCount,
              displayStatus
            };
          } catch (err) {
            console.error(`Failed to get details for case ${caseItem.id}:`, err);
            return { ...caseItem, displayStatus: caseItem.status };
          }
        })
      );
      
      return casesWithDetails;
    }
  );

  // Fetch recent reports
  const { data: recentReports, isLoading: reportsLoading } = useQuery(
    'recentReports',
    async () => {
      try {
        const reports = await reportApi.getUserReports();
        return reports
          .slice(0, 5)
          .sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
      } catch (err) {
        console.error('Failed to fetch recent reports:', err);
        return [];
      }
    }
  );

  const isLoading = statsLoading || casesLoading || reportsLoading;

  const hasNoCases = !stats?.totalCases;

  if (isLoading) {
    return (
      <div className="p-6 dark:bg-medical-900">
        <div className="flex items-center justify-center h-64">
          <Loader className="h-8 w-8 animate-spin text-primary-600" />
          <span className="ml-2 text-medical-600 dark:text-medical-400">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 dark:bg-medical-900">
      {/* Welcome Header */}
      <div>
        <h1 className="text-2xl font-bold text-medical-900 dark:text-medical-100">
          {`Welcome, ${welcomeName}`}
        </h1>
        <p className="text-medical-600 dark:text-medical-400">
          Here's what's happening with your cases and reports
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="card dark:bg-medical-800 dark:border-medical-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-medical-600 dark:text-medical-400">Total Cases</p>
              <p className="text-2xl font-bold text-medical-900 dark:text-medical-100">{stats?.totalCases || 0}</p>
            </div>
            <Folder className="h-8 w-8 text-primary-500" />
          </div>
        </div>

        <div className="card dark:bg-medical-800 dark:border-medical-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-medical-600 dark:text-medical-400">Pages Processed</p>
              <p className="text-2xl font-bold text-medical-900 dark:text-medical-100">{stats?.totalPages?.toLocaleString() || 0}</p>
            </div>
            <FileText className="h-8 w-8 text-indigo-500" />
          </div>
        </div>

        <div className="card dark:bg-medical-800 dark:border-medical-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-medical-600 dark:text-medical-400">Total Drafts</p>
              <p className="text-2xl font-bold text-medical-900 dark:text-medical-100">{stats?.totalReports || 0}</p>
            </div>
            <FileCheck className="h-8 w-8 text-green-500" />
          </div>
        </div>

        {(stats?.creditsRemaining ?? 0) > 0 && (
          <div className="card dark:bg-medical-800 dark:border-medical-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-medical-600 dark:text-medical-400">Credits Remaining</p>
                <p className={`text-2xl font-bold ${stats!.creditsRemaining <= 10 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                  {stats!.creditsRemaining.toLocaleString()}
                </p>
              </div>
              <CreditCard className="h-8 w-8 text-primary-500" />
            </div>
          </div>
        )}

        <div className="card dark:bg-medical-800 dark:border-medical-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-medical-600 dark:text-medical-400">Documents</p>
              <p className="text-2xl font-bold text-medical-900 dark:text-medical-100">{stats?.totalDocuments || 0}</p>
            </div>
            <FileText className="h-8 w-8 text-cyan-500" />
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card dark:bg-medical-800 dark:border-medical-700">
        <h2 className="text-lg font-semibold text-medical-900 dark:text-medical-100 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {hasNoCases ? (
            <>
              <Link
                to="/help"
                className="flex items-center space-x-3 p-4 border-2 border-dashed border-primary-400 dark:border-primary-600 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:border-primary-500 hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
              >
                <BookOpen className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                <div>
                  <p className="font-medium text-medical-900 dark:text-medical-100">Getting Started</p>
                  <p className="text-sm text-medical-600 dark:text-medical-400">Learn how to create cases and run analysis</p>
                </div>
              </Link>

              <button
                onClick={handleNewCase}
                disabled={createCaseMutation.isLoading}
                className="flex items-center space-x-3 p-4 border-2 border-dashed border-medical-300 dark:border-medical-600 rounded-lg hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors text-left"
              >
                {createCaseMutation.isLoading ? (
                  <Loader className="h-6 w-6 text-primary-600 dark:text-primary-400 animate-spin" />
                ) : (
                  <Plus className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                )}
                <div>
                  <p className="font-medium text-medical-900 dark:text-medical-100">New Case</p>
                  <p className="text-sm text-medical-600 dark:text-medical-400">Start a new case review</p>
                </div>
              </button>

              <Link
                to="/templates"
                className="flex items-center space-x-3 p-4 border-2 border-dashed border-medical-300 dark:border-medical-600 rounded-lg hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
              >
                <FileText className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                <div>
                  <p className="font-medium text-medical-900 dark:text-medical-100">Browse Templates</p>
                  <p className="text-sm text-medical-600 dark:text-medical-400">View report templates</p>
                </div>
              </Link>
            </>
          ) : (
            <>
              <button
                onClick={handleNewCase}
                disabled={createCaseMutation.isLoading}
                className="flex items-center space-x-3 p-4 border-2 border-dashed border-medical-300 dark:border-medical-600 rounded-lg hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors text-left"
              >
                {createCaseMutation.isLoading ? (
                  <Loader className="h-6 w-6 text-primary-600 dark:text-primary-400 animate-spin" />
                ) : (
                  <Plus className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                )}
                <div>
                  <p className="font-medium text-medical-900 dark:text-medical-100">New Case</p>
                  <p className="text-sm text-medical-600 dark:text-medical-400">Start a new case review</p>
                </div>
              </button>

              <Link
                to="/cases"
                className="flex items-center space-x-3 p-4 border-2 border-dashed border-medical-300 dark:border-medical-600 rounded-lg hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
              >
                <Search className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                <div>
                  <p className="font-medium text-medical-900 dark:text-medical-100">Find a Case</p>
                  <p className="text-sm text-medical-600 dark:text-medical-400">Browse and search your cases</p>
                </div>
              </Link>

              <Link
                to="/templates"
                className="flex items-center space-x-3 p-4 border-2 border-dashed border-medical-300 dark:border-medical-600 rounded-lg hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
              >
                <FileText className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                <div>
                  <p className="font-medium text-medical-900 dark:text-medical-100">Browse Templates</p>
                  <p className="text-sm text-medical-600 dark:text-medical-400">View report templates</p>
                </div>
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Cases */}
        <div className="card dark:bg-medical-800 dark:border-medical-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-medical-900 dark:text-medical-100">Recent Cases</h2>
            <Link to="/cases" className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 flex items-center space-x-1">
              <span>View all</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {recentCases && recentCases.length > 0 ? (
            <div className="space-y-3">
              {recentCases.map((caseItem: any) => (
                <Link
                  key={caseItem.id}
                  to={`/cases/${caseItem.id}`}
                  className="block p-3 border border-medical-200 dark:border-medical-600 rounded-lg hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <p className="font-medium text-medical-900 dark:text-medical-100 truncate flex-1">{caseItem.name}</p>
                    <div className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ml-2 ${getStatusColor(caseItem.displayStatus)}`}>
                      {getStatusIcon(caseItem.displayStatus)}
                      <span>{getStatusText(caseItem.displayStatus)}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 text-xs text-medical-500 dark:text-medical-400">
                    <span className="flex items-center space-x-1">
                      <FileText className="h-3 w-3" />
                      <span>{caseItem.documentCount || 0} docs</span>
                    </span>
                    <span>
                      {formatDistanceToNow(new Date(caseItem.updatedAt), { addSuffix: true })}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Folder className="h-12 w-12 text-medical-300 dark:text-medical-600 mx-auto mb-3" />
              <p className="text-medical-600 dark:text-medical-400 mb-4">No cases yet</p>
              <button onClick={handleNewCase} disabled={createCaseMutation.isLoading} className="btn-primary">
                {createCaseMutation.isLoading ? 'Creating...' : 'Create First Case'}
              </button>
            </div>
          )}
        </div>

        {/* Recent Drafts / Onboarding */}
        {hasNoCases ? (
          <div className="card dark:bg-medical-800 dark:border-medical-700">
            <div className="flex items-center space-x-2 mb-4">
              <BookOpen className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              <h2 className="text-lg font-semibold text-medical-900 dark:text-medical-100">How It Works</h2>
            </div>

            <div className="space-y-3 mb-5">
              {[
                { step: '1', icon: <Plus className="h-4 w-4" />, title: 'Create a Case', desc: 'Set up your medico-legal case with patient and case details' },
                { step: '2', icon: <Upload className="h-4 w-4" />, title: 'Upload Records', desc: 'Upload medical records, scans, and clinical documents' },
                { step: '3', icon: <Sparkles className="h-4 w-4" />, title: 'Run AI Analysis', desc: 'AI extracts timelines, identifies gaps, and detects inconsistencies' },
                { step: '4', icon: <FileCheck className="h-4 w-4" />, title: 'Drafting', desc: 'Draft court-ready documents — reports, letters of instruction, and more — with full source citations' },
              ].map(item => (
                <div key={item.step} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 flex items-center justify-center text-xs font-bold">
                    {item.step}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-medical-900 dark:text-medical-100">{item.title}</p>
                    <p className="text-xs text-medical-600 dark:text-medical-400">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-medical-200 dark:border-medical-600 pt-4 mb-4">
              <p className="text-xs font-semibold text-medical-500 dark:text-medical-400 uppercase tracking-wider mb-2">Analysis Types</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { icon: <BarChart3 className="h-3.5 w-3.5" />, label: 'Chronology', color: 'text-cyan-600 dark:text-cyan-400' },
                  { icon: <Activity className="h-3.5 w-3.5" />, label: 'Standard of Care', color: 'text-green-600 dark:text-green-400' },
                  { icon: <Target className="h-3.5 w-3.5" />, label: 'Causation', color: 'text-red-600 dark:text-red-400' },
                  { icon: <Scale className="h-3.5 w-3.5" />, label: 'Comparative', color: 'text-indigo-600 dark:text-indigo-400' },
                  { icon: <Search className="h-3.5 w-3.5" />, label: 'Gap Analysis', color: 'text-amber-600 dark:text-amber-400' },
                  { icon: <AlertTriangle className="h-3.5 w-3.5" />, label: 'Inconsistencies', color: 'text-orange-600 dark:text-orange-400' },
                ].map(item => (
                  <div key={item.label} className={`flex items-center space-x-1.5 text-xs ${item.color}`}>
                    {item.icon}
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <button onClick={handleNewCase} disabled={createCaseMutation.isLoading} className="btn-primary inline-flex items-center space-x-2">
              <span>{createCaseMutation.isLoading ? 'Creating...' : 'Create Your First Case'}</span>
              {!createCaseMutation.isLoading && <ArrowRight className="h-4 w-4" />}
            </button>
          </div>
        ) : (
          <div className="card dark:bg-medical-800 dark:border-medical-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-medical-900 dark:text-medical-100">Recent Drafts</h2>
              <Link to="/drafts" className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 flex items-center space-x-1">
                <span>View all</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            {recentReports && recentReports.length > 0 ? (
              <div className="space-y-3">
                {recentReports.map((report: any) => (
                  <Link
                    key={report.id}
                    to={`/cases/${report.caseId}/overview`}
                    state={{ openReportId: report.id }}
                    className="block p-3 border border-medical-200 dark:border-medical-600 rounded-lg hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-medical-900 dark:text-medical-100 truncate">{report.title}</p>
                        <p className="text-xs text-medical-500 dark:text-medical-400 mt-1">
                          {formatDistanceToNow(new Date(report.createdAt), { addSuffix: true })}
                        </p>
                      </div>
                      <FileCheck className="h-4 w-4 text-green-500 flex-shrink-0 ml-2" />
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileCheck className="h-12 w-12 text-medical-300 dark:text-medical-600 mx-auto mb-3" />
                <p className="text-medical-600 dark:text-medical-400 mb-4">No drafts yet</p>
                <Link to="/drafts" className="btn-primary">
                  View Drafts
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
